/*
* Created on Feb 24, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import React, {useState, useReducer, useEffect} from 'react';
import TableComponent from "../common/TableComponent/TableComponent";
import ic_content_device_svg from "../../static/img/content/ic-content-device.svg";
import {useQuery} from "@tanstack/react-query";
import {useOutletContext, useParams} from "react-router-dom";
import DeviceTableRowComponent from "./DeviceTableRowComponent";
import {useAuthSession} from "../../context/AuthSessionContext";
import {useTranslation} from "react-i18next";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useOrganization} from "../../context/OrganizationContext";
import useEmailChangedHook from "../../hook/EmailChangedHook";
import DeviceAddAdminModal from "./DeviceAddAdminModal";
import DeviceTransferToQid from "./transfer/DeviceTransferToQID";
import DeviceTransferToQidSuccess from "./transfer/DeviceTransferToQIDSuccess";
import DeviceTransferToQidFail from "./transfer/DeviceTransferToQIDFail";
import Cookies from "js-cookie";
import DeviceEmptyContent from "./DeviceEmptyContent";

export const collectOwnerIds = (orgUsers) => {
    return new Set(
        (orgUsers || [])
            .filter(u => u?.is_owner || u?.role === 'owner')
            .map(u => u?.id)
            .filter(Boolean)
    );
};

const adminDisplayName = (user) => {
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    const primaryEmail = (user?.emails || []).find(e => e?.is_primary)?.email || (user?.emails || [])[0]?.email;
    return user?.display_name || fullName || primaryEmail || user?.email || '';
};

export const getAdminTags = (input, orgUsers) => {
    const ownerIds = collectOwnerIds(orgUsers);
    const users = (input?.organization_users || []).filter(u => !ownerIds.has(u?.id));
    const groups = (input?.organization_groups || []);
    const userTags = users.map(adminDisplayName).filter(Boolean);
    const groupTags = groups.map(group => group.name);
    return [...userTags, ...groupTags];
};

const table_ui = {
    toolbar: {
        count: true,
        create_button: false,
        delete_button: false,
        export_button: false
    },
    table_field_checkbox: false
}

const DeviceIndexComponent = () => {
    const { userPermissions} = useOutletContext();
    const {t} =useTranslation();
    const {OrganizationAPI} = useOrganizationApi();
    const {useCheckSession} = useAuthSession();
    const {getCurrentOrg} = useOrganization();
    const {oid} = useParams();
    const [page, setPage] = useState({limit:10, offset:0})
    const [refresh, setRefresh] = useState(0)
    const {EmailWarningDialog} = useEmailChangedHook();
    const {data:deviceList, isSuccess} = useQuery({
        queryKey: [`/v1.2/device`, oid, page, refresh],
        queryFn: async ({signal}) => {
            const device_list = await OrganizationAPI.device.get('', {...page, organization_id: oid}, signal);

            let data_list = [];
            if (device_list && device_list?.result?.length > 0) {
                const source_user_ids = [...new Set(device_list.result.map(device => device.source_user_id).filter(user_id => !!user_id))] || [];
                const users = await fetchUsers({type: 'id', users: source_user_ids});
                let user_map = {};
                if (users?.result?.users?.length > 0) {
                    for (const user of users.result.users) {
                        user_map[user.user_id] = user;
                    }
                }
                for await (const device of device_list?.result) {
                    const whitelist = await fetchDeviceWhitelist(device.device_id);
                    device.user_whitelist = whitelist;
                    device.source_user = user_map[device.source_user_id];
                    data_list.push(device);
                }
                return data_list;
            }
            return device_list.result;
        }
    });

    const {data:deviceSummary} = useQuery({
        queryKey: [`/v1.2/device/summary`, refresh],
        queryFn: async ({signal}) => {
            const device_list = await OrganizationAPI.device.get('summary', {organization_id: oid}, signal);
            return device_list.result;
        }
    });

    const table_head_fields = [
        {field_name: t('@Device Name@'), isSortable: true, width_style: '!w-15', field_id: 'device_name', default_sort: true, default_sort_order: ''},
        // {field_name: 'Status', isSortable: false, width_style: '!w-10'},
        {field_name: t('@Model@'), isSortable: true, width_style: '!w-12', field_id: 'model_info.name', default_sort: false, default_sort_order: ''},
        {field_name: t('@Firmware@'), isSortable: false, width_style: '!w-14'},
        {field_name: t('@Site@'), isSortable: false, width_style: '!w-10'},
        {field_name: t('@Serial Number@'), isSortable: false, width_style: '!w-14'},
        {field_name: t('@Administrator@'), isSortable: false, width_style: '!w-20'},
        {field_name: t('@Registered by@'), isSortable: false, width_style: '!w-14'},
    ];

    const fetchDeviceWhitelist = async (device_id) => {
        try {
            const controller = new AbortController();
            const signal = controller.signal;
            const data = await OrganizationAPI.device.get(`${device_id}/organization_whitelist`, {}, signal);
            let result = {};
            result.organization_users = (data?.result?.organization_users || []);
            result.organization_groups = (data?.result?.organization_groups || []);
            result.tags = getAdminTags(data?.result || {}, getCurrentOrg()?.users);
            return result;
        } catch (e) {
            console.error(`[DeviceIndex] fetchDeviceWhitelist failed for device ${device_id}:`, e);
            return { organization_users: [], organization_groups: [], tags: [] };
        }
    }

    const refreshCsrfToken = async () => {
        try {
            await fetch('/ajax/csrf-token', { credentials: 'same-origin' });
        } catch (e) {
            console.error('[DeviceIndex] Failed to refresh CSRF token:', e);
        }
    }

    const fetchUsers = async (data, isRetry = false) => {
         try {
            const users = await fetch(`/ajax/users`, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    'X-CSRF-Token': Cookies.get('XSRF-TOKEN')
                },
                body: JSON.stringify(data)
            })
            if (users.status === 403 && !isRetry) {
                const errorBody = await users.json();
                if (errorBody?.code === 'invalid_csrf_token') {
                    await refreshCsrfToken();
                    return fetchUsers(data, true);
                }
                return errorBody;
            }
            const users_json = await users.json();
            return users_json;
        } catch (e) {
            console.log(e)
        }
    }

    const dispatch_handler = (device_state, action) => {
        switch (action.type) {
            case 'init':
                device_state.data = action.payload;
                device_state.select_box = false;
                device_state.admin_modal = false;
                device_state.transfer_model = false;
                device_state.transfer_success = false;
                device_state.transfer_fail = false;
                device_state.search = (obj, query) => {
                    return obj.device_name.match(new RegExp(query, 'i'))
                };
                device_state.userPermissions = userPermissions;
                return {...device_state};
            case 'get_total':
                return {...device_state, total: action.payload}
            case 'search_device':
                return {
                    ...device_state,
                    query: action.payload
                };
            case 'toggle_admin':
                return {
                    ...device_state,
                    admin_modal: action.payload.open,
                    device_data: action.payload.device_data
                }
            case 'toggle_contact_admin':
                return {
                    ...device_state,
                    device_data: {
                        ...device_state.device_data,
                        user_whitelist: {
                            ...device_state.device_data.user_whitelist,
                            organization_users: action.payload.organization_users,
                            organization_groups: action.payload.organization_groups,
                        }
                    }
                }
            case 'toggle_transfer':
                return {
                    ...device_state,
                    transfer_model: action.payload.open,
                    device_data: action.payload.data,
                    firmware: action.payload.firmware
                }
            case 'toggle_transfer_success':
                return {
                    ...device_state,
                    transfer_model: false,
                    transfer_success: action.payload.open,
                    organization: action.payload.data
                }
            case 'toggle_transfer_fail':
                return {
                    ...device_state,
                    transfer_model: false,
                    transfer_fail: action.payload.open,
                    organization: action.payload.data
                }
            case 'pagination':
                setPage({limit:action.payload.limit, offset:action.payload.offset});
                if (action.payload.refresh) {
                    setRefresh(prev=>prev+1);
                }
                return {...device_state};
            default:
                return device_state;
        }
    }

    const [state, dispatch] = useReducer(dispatch_handler, {});

    useEffect(()=>{
        if (deviceList) {
            dispatch({type: 'init', payload: deviceList});
        }
    }, [refresh, deviceList]);

    useEffect(()=>{
        dispatch({type: 'get_total', payload: deviceSummary?.count});
    }, [refresh, deviceSummary])
    useCheckSession();

    return (
        <>
            <div className="content content-page">
                <TableComponent
                    name="Device"
                    table_icon={ic_content_device_svg}
                    table_head_fields={table_head_fields}
                    table_data={state}
                    state={state}
                    total_record={state.total}
                    search={true}
                    disPatcher={dispatch}
                    row_component={DeviceTableRowComponent}
                    table_ui={table_ui}
                    empty_data={<DeviceEmptyContent />}
                    data_table_name='devices-table'
                >
                </TableComponent>
            </div>
            {isSuccess && <EmailWarningDialog />}
            {state.admin_modal && <DeviceAddAdminModal openModal={state.admin_modal} closeModal={()=>dispatch({type: 'toggle_admin', payload: {open:false, data: {}}})} device_id={state.device_data.device_id} adminList={state.device_data.user_whitelist} dispatch={dispatch} state={state}/>}
            {state.transfer_model && <DeviceTransferToQid openModal={state.transfer_model} closeModal={()=>dispatch({type: 'toggle_transfer', payload: {open:false}})} device={state.device_data} dispatch={dispatch} state={state}/>}
            {state.transfer_success && <DeviceTransferToQidSuccess openModal={state.transfer_success} closeModal={()=>dispatch({type: 'toggle_transfer_success', payload: {open:false}})} device={state.device_data} dispatch={dispatch} state={state}/>}
            {state.transfer_fail && <DeviceTransferToQidFail openModal={state.transfer_fail} closeModal={()=>dispatch({type: 'toggle_transfer_fail', payload: {open:false}})} device={state.device_data} dispatch={dispatch} state={state}/>}
        </>
    );
};

export default DeviceIndexComponent;